import { action, Action, Thunk, thunk } from "easy-peasy";
import { Injections } from "../../../store";
import { StoreModel } from "../..";
import {
  EnumProductTypology,
  SearchableDateRangeFilterInput,
  SearchableStringFilterInput,
} from "../../../api/graphql/types";
import { IGetResponse, IListResponse, IMultiLanguage } from "..";
import { Quote } from "../Quotes";

export interface QuoteRequestStatus {
  id: number;
  code: number;
  label: IMultiLanguage;
  disabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface QuoteRequestService {
  id: number;
  code: string;
  label: IMultiLanguage;
  description?: IMultiLanguage;
  required: boolean;
  is_bookable: boolean;
  is_special_event: boolean;
  should_select_product: boolean;
  category_id?: string;
  created_at: string;
  updated_at: string;
  minimum_approval_time?: number;
}

export interface QuoteRequestServiceSelection {
  id: number;
  service: QuoteRequestService;
  start_date?: string;
  end_date?: string;
  total_participants?: number;
  quantity?: number;
  product: {
    id: string;
    name: IMultiLanguage;
    typology: EnumProductTypology;
  };
  description?: IMultiLanguage;
  created_at: string;
  updated_at: string;
}

export interface IUser {
  id: string;
  last_name: string;
  first_name: string;
  email: string;
}

export interface QuoteRequest {
  id: number;
  ref: string;
  external_ref?: string | null;
  services: QuoteRequestService[];
  selections?: QuoteRequestServiceSelection[];
  building: {
    id: string;
  };
  title?: IMultiLanguage;
  description?: IMultiLanguage;
  priority: number;
  status: QuoteRequestStatus;
  can_create_quote: boolean;
  next_status: number[];
  user: IUser;
  back_office_user?: IUser;
  quotes?: Quote[];
  created_at: string;
  updated_at: string;
}

export interface GetQuoteRequestOpts {
  id: string;
}

export interface UpdateQuoteRequestOpts {
  id: number;
  status_code?: number;
  external_ref?: string;
  high_priority?: boolean;
}

export type SearchableQuoteRequestsFilterInput = {
  status?: SearchableStringFilterInput;
  service?: SearchableStringFilterInput;
  lastname?: SearchableStringFilterInput;
  created_at?: SearchableDateRangeFilterInput;
};

export interface GetQuoteRequestStatusOpts {
  id: string;
}

export interface GetQuoteRequestServiceOpts {
  id: string;
}

export interface DeleteQuoteRequestOpts {
  id: number;
}

export interface DeleteQuoteRequestStatusOpts {
  id: number;
}

export interface DeleteQuoteRequestServiceOpts {
  id: number;
}

export interface CreateQuoteRequestStatusOpts {
  code: number;
  label: IMultiLanguage;
}

export interface CreateQuoteRequestServiceOpts {
  code: string;
  label: IMultiLanguage;
  required: boolean;
  description?: IMultiLanguage;
  is_special_event?: boolean;
  is_bookable?: boolean;
  minimum_approval_time?: number;
  should_select_product?: boolean;
  category_id?: string;
}

export interface UpdateQuoteRequestStatusOpts {
  id: number;
  code?: number;
  label?: IMultiLanguage;
}

export interface UpdateQuoteRequestServiceOpts {
  id: number;
  code?: string;
  label?: IMultiLanguage;
  required?: boolean;
  description?: IMultiLanguage;
  is_special_event?: boolean;
  is_bookable?: boolean;
  minimum_approval_time?: number;
  should_select_product?: boolean;
  category_id?: string;
}

export interface ListQuoteRequestStatusOpts {
  limit: number;
  offset: number;
}

export interface ListQuoteRequestServicesOpts {
  limit: number;
  offset: number;
}

export interface ListQuoteRequestsOpts {
  buildingId: string;
  limit: number;
  offset: number;
  filter?: SearchableQuoteRequestsFilterInput;
}

export interface QuoteRequestModel {
  quoteRequests: {
    items: QuoteRequest[];
    total: number;
    has_more: boolean;
  };
  quoteRequestStatuss: {
    items: QuoteRequestStatus[];
    total: number;
    has_more: boolean;
  };
  quoteRequestServices: {
    items: QuoteRequestService[];
    total: number;
    has_more: boolean;
  };
  quoteRequest: QuoteRequest | null;
  quoteRequestStatus: QuoteRequestStatus | null;
  quoteRequestService: QuoteRequestService | null;

  setQuoteRequests: Action<
    QuoteRequestModel,
    {
      items: QuoteRequest[];
      total: number;
      has_more: boolean;
    }
  >;
  setQuoteRequestStatuss: Action<
    QuoteRequestModel,
    { items: QuoteRequestStatus[]; total: number; has_more: boolean }
  >;
  setQuoteRequestServices: Action<
    QuoteRequestModel,
    { items: QuoteRequestService[]; total: number; has_more: boolean }
  >;

  setQuotRequest: Action<QuoteRequestModel, QuoteRequest | null>;
  setQuoteRequestStatus: Action<QuoteRequestModel, QuoteRequestStatus | null>;
  setQuoteRequestService: Action<QuoteRequestModel, QuoteRequestService | null>;

  removeQuoteRequest: Action<QuoteRequestModel, DeleteQuoteRequestOpts>;
  removeQuoteRequestStatus: Action<
    QuoteRequestModel,
    DeleteQuoteRequestStatusOpts
  >;
  removeQuoteRequestService: Action<
    QuoteRequestModel,
    DeleteQuoteRequestServiceOpts
  >;

  // QuoteRequest
  listQuoteRequests: Thunk<
    QuoteRequestModel,
    ListQuoteRequestsOpts,
    Injections,
    StoreModel
  >;
  getQuoteRequest: Thunk<
    QuoteRequestModel,
    GetQuoteRequestOpts,
    Injections,
    StoreModel
  >;
  updateQuoteRequest: Thunk<
    QuoteRequestModel,
    UpdateQuoteRequestOpts,
    Injections,
    StoreModel
  >;
  deleteQuoteRequest: Thunk<
    QuoteRequestModel,
    DeleteQuoteRequestOpts,
    Injections,
    StoreModel
  >;

  // QuoteRequestStatus
  listQuoteRequestStatus: Thunk<
    QuoteRequestModel,
    ListQuoteRequestStatusOpts | undefined,
    Injections,
    StoreModel
  >;
  getQuoteRequestStatus: Thunk<
    QuoteRequestModel,
    GetQuoteRequestStatusOpts,
    Injections,
    StoreModel
  >;
  createQuoteRequestStatus: Thunk<
    QuoteRequestModel,
    CreateQuoteRequestStatusOpts,
    Injections,
    StoreModel
  >;
  updateQuoteRequestStatus: Thunk<
    QuoteRequestModel,
    UpdateQuoteRequestStatusOpts,
    Injections,
    StoreModel
  >;
  deleteQuoteRequestStatus: Thunk<
    QuoteRequestModel,
    DeleteQuoteRequestStatusOpts,
    Injections,
    StoreModel
  >;

  // QuoteRequestService
  listQuoteRequestServices: Thunk<
    QuoteRequestModel,
    ListQuoteRequestServicesOpts | undefined,
    Injections,
    StoreModel
  >;
  getQuoteRequestService: Thunk<
    QuoteRequestModel,
    GetQuoteRequestServiceOpts,
    Injections,
    StoreModel
  >;
  createQuoteRequestService: Thunk<
    QuoteRequestModel,
    CreateQuoteRequestServiceOpts,
    Injections,
    StoreModel
  >;
  updateQuoteRequestService: Thunk<
    QuoteRequestModel,
    UpdateQuoteRequestServiceOpts,
    Injections,
    StoreModel
  >;
  deleteQuoteRequestService: Thunk<
    QuoteRequestModel,
    DeleteQuoteRequestServiceOpts,
    Injections,
    StoreModel
  >;
}

export const quoteRequestModel: QuoteRequestModel = {
  quoteRequests: { items: [], total: 0, has_more: false },
  quoteRequestStatuss: { items: [], total: 0, has_more: false },
  quoteRequestServices: { items: [], total: 0, has_more: false },

  quoteRequest: null,
  quoteRequestStatus: null,
  quoteRequestService: null,

  setQuoteRequests: action((state, payload) => {
    state.quoteRequests = payload;
  }),
  setQuoteRequestStatuss: action((state, payload) => {
    state.quoteRequestStatuss = payload;
  }),
  setQuoteRequestServices: action((state, payload) => {
    state.quoteRequestServices = payload;
  }),

  setQuotRequest: action((state, payload: QuoteRequest) => {
    state.quoteRequest = payload;
  }),
  setQuoteRequestStatus: action((state, payload: QuoteRequestStatus) => {
    state.quoteRequestStatus = payload;
  }),
  setQuoteRequestService: action((state, payload: QuoteRequestService) => {
    state.quoteRequestService = payload;
  }),

  removeQuoteRequest: action((state, payload) => {
    state.quoteRequests.items = state.quoteRequests.items.filter(
      p => p.id !== payload.id
    );
    state.quoteRequests.total = state.quoteRequests.total - 1;
  }),
  removeQuoteRequestStatus: action((state, payload) => {
    state.quoteRequestStatuss.items = state.quoteRequestStatuss.items.filter(
      p => p.id !== payload.id
    );
    state.quoteRequestStatuss.total = state.quoteRequestStatuss.total - 1;
  }),
  removeQuoteRequestService: action((state, payload) => {
    state.quoteRequestServices.items = state.quoteRequestServices.items.filter(
      p => p.id !== payload.id
    );
    state.quoteRequestServices.total = state.quoteRequestServices.total - 1;
  }),

  getQuoteRequest: thunk(
    async (actions, payload: GetQuoteRequestOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.getQuoteRequest(payload.id);
      if (response.status === 200 && response.data.success) {
        actions.setQuotRequest(response.data.data);
      }
    }
  ),
  updateQuoteRequest: thunk(
    async (
      actions,
      payload: UpdateQuoteRequestOpts,
      { injections, getState }
    ) => {
      const { quoteRequestService } = injections;

      const response = await quoteRequestService.updateQuoteRequest(payload);

      if (response.status === 200 && response.data.success) {
        const data = response.data.data;
        actions.setQuotRequest(data);

        const state = getState();
        const index = state.quoteRequests.items.findIndex(
          q => q.id === data.id
        );

        if (index !== -1) {
          const newItems = [...state.quoteRequests.items];
          newItems.splice(index, 1, data);

          actions.setQuoteRequests({
            ...state.quoteRequests,
            items: newItems,
          });
        }
      }

      return response.data;
    }
  ),
  listQuoteRequests: thunk(
    async (actions, payload: ListQuoteRequestsOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const { buildingId, offset, limit, filter } = payload;
      const response = await quoteRequestService.listQuoteRequests(
        buildingId,
        offset,
        limit,
        filter
      );
      if (response.status === 200 && response.data.success) {
        actions.setQuoteRequests(response.data.data);
      }
    }
  ),
  deleteQuoteRequest: thunk(
    async (actions, payload: DeleteQuoteRequestOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.deleteQuoteRequest(payload.id);
      if (response.status === 200 && response.data.success) {
        actions.setQuotRequest(null);
        actions.removeQuoteRequest(payload);
      }
    }
  ),

  listQuoteRequestStatus: thunk(
    async (actions, payload: ListQuoteRequestServicesOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.listQuoteRequestStatus(
        payload
      );
      if (response.status === 200 && response.data.success) {
        const { data }: IListResponse<QuoteRequestStatus> = response.data;
        actions.setQuoteRequestStatuss(data);

        return data;
      }
    }
  ),
  getQuoteRequestStatus: thunk(
    async (actions, payload: GetQuoteRequestStatusOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.getQuoteRequestStatus(
        payload.id
      );
      if (response.status === 200 && response.data.success) {
        const quoteRequestStatus: IGetResponse<QuoteRequestStatus> =
          response.data;
        actions.setQuoteRequestStatus(quoteRequestStatus.data);

        return quoteRequestStatus.data;
      }
    }
  ),
  createQuoteRequestStatus: thunk(
    async (actions, payload: CreateQuoteRequestStatusOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.createQuoteRequestStatus(
        payload
      );
      if (response.status === 201 && response.data.success) {
        actions.setQuoteRequestStatus(response.data.data);
      }
    }
  ),
  updateQuoteRequestStatus: thunk(
    async (
      actions,
      payload: UpdateQuoteRequestStatusOpts,
      { injections, getState }
    ) => {
      const { quoteRequestService } = injections;

      const response = await quoteRequestService.updateQuoteRequestStatus(
        payload
      );

      if (response.status === 200 && response.data.success) {
        const data = response.data.data;
        actions.setQuoteRequestStatus(data);

        const state = getState();
        const index = state.quoteRequestStatuss.items.findIndex(
          p => p.id === data.id
        );

        if (index !== -1) {
          const newItems = [...state.quoteRequestStatuss.items];
          newItems.splice(index, 1, data);

          actions.setQuoteRequestStatuss({
            ...state.quoteRequestStatuss,
            items: newItems,
          });
        }
      }

      return response.data;
    }
  ),
  deleteQuoteRequestStatus: thunk(
    async (actions, payload: DeleteQuoteRequestStatusOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.deleteQuoteRequestStatus(
        payload.id
      );
      if (response.status === 200 && response.data.success) {
        actions.setQuoteRequestStatus(null);
        actions.removeQuoteRequestStatus(payload);
      }
    }
  ),

  listQuoteRequestServices: thunk(
    async (actions, payload: ListQuoteRequestServicesOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.listQuoteRequestServices(
        payload
      );
      if (response.status === 200 && response.data.success) {
        const quoteRequestServices: IListResponse<QuoteRequestService> =
          response.data;
        actions.setQuoteRequestServices(quoteRequestServices.data);

        return quoteRequestServices.data;
      }
    }
  ),
  getQuoteRequestService: thunk(
    async (actions, payload: GetQuoteRequestServiceOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.getQuoteRequestService(
        payload.id
      );
      if (response.status === 200 && response.data.success) {
        const { data }: IGetResponse<QuoteRequestService> = response.data;
        actions.setQuoteRequestService(data);

        return data;
      }
    }
  ),
  createQuoteRequestService: thunk(
    async (actions, payload: CreateQuoteRequestServiceOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.createQuoteRequestService(
        payload
      );
      if (response.status === 201 && response.data.success) {
        actions.setQuoteRequestService(response.data.data);
      }
    }
  ),
  updateQuoteRequestService: thunk(
    async (
      actions,
      payload: UpdateQuoteRequestServiceOpts,
      { injections, getState }
    ) => {
      const { quoteRequestService } = injections;

      const response = await quoteRequestService.updateQuoteRequestService(
        payload
      );

      if (response.status === 200 && response.data.success) {
        const data = response.data.data;
        actions.setQuoteRequestService(data);

        const state = getState();
        const index = state.quoteRequestServices.items.findIndex(
          p => p.id === data.id
        );

        if (index !== -1) {
          const newItems = [...state.quoteRequestServices.items];
          newItems.splice(index, 1, data);

          actions.setQuoteRequestServices({
            ...state.quoteRequestServices,
            items: newItems,
          });
        }
      }

      return response.data;
    }
  ),
  deleteQuoteRequestService: thunk(
    async (actions, payload: DeleteQuoteRequestOpts, { injections }) => {
      const { quoteRequestService } = injections;
      const response = await quoteRequestService.deleteQuoteRequestService(
        payload.id
      );
      if (response.status === 200 && response.data.success) {
        actions.setQuoteRequestService(null);
        actions.removeQuoteRequestService(payload);
      }
    }
  ),
};
